/**
 * Members' Fun section (docs/FUN_SECTION.md): hub, character page and shared bits.
 * Players are paired kid devices; everything personal comes from /api/kid/fun.
 */
import { LitElement, css, html, nothing, svg } from 'lit';
import {
  ACTIVITIES,
  EQUIP_SLOTS,
  REWARDS,
  faceRect,
  nextToEarn,
  rewardById,
  type ActivityId,
  type EquipSlot,
  type FunProgress,
  type RewardItem,
} from '@ka/fun';
import { shared, spiderSvg } from '@ka/ui';
import { currentKid, kidApi } from './kid.ts';

export interface FunView {
  equipped: Partial<Record<EquipSlot, string>>;
  selectedSkinId: string | null;
  progress: FunProgress;
  granted: string[];
}

let fun: FunView | null = null;
export const currentFun = () => fun;

/** Loads (and refreshes) the kid's Fun data; null when the device is not paired. */
export async function loadFun(): Promise<FunView | null> {
  if (!currentKid()) return (fun = null);
  fun = (await kidApi<{ fun: FunView }>('GET', '/fun')).fun;
  return fun;
}
export function setFun(f: FunView) {
  fun = f;
  window.dispatchEvent(new CustomEvent('ka-fun-changed'));
}

// ---------- original pixel icons (8×8) ----------

const GLYPHS: Record<Exclude<RewardItem['glyph'], 'spider'>, string[]> = {
  circuit: [
    '........',
    '.##..##.',
    '.#....#.',
    '.#.##.#.',
    '.#.##.#.',
    '.#....#.',
    '.##..##.',
    '........',
  ],
  lamp: [
    '..####..',
    '.#....#.',
    '#..##..#',
    '#.#..#.#',
    '#..##..#',
    '.#....#.',
    '..####..',
    '...##...',
  ],
  palette: [
    '..####..',
    '.#....#.',
    '#.#..#.#',
    '#......#',
    '#.#...#.',
    '#....##.',
    '.#..#...',
    '..##....',
  ],
  blueprint: [
    '...##...',
    '..####..',
    '.######.',
    '########',
    '.#....#.',
    '.#.##.#.',
    '.#.##.#.',
    '.######.',
  ],
  cart: [
    '........',
    '#......#',
    '########',
    '#.#..#.#',
    '########',
    '.######.',
    '.##..##.',
    '.##..##.',
  ],
  rail: [
    '.#....#.',
    '########',
    '.#....#.',
    '.#....#.',
    '########',
    '.#....#.',
    '.#....#.',
    '########',
  ],
  backpack: [
    '..####..',
    '.#....#.',
    '########',
    '#.####.#',
    '#.#..#.#',
    '#.####.#',
    '#......#',
    '########',
  ],
  compass: [
    '..####..',
    '.#..#.#.',
    '#...#..#',
    '#..##..#',
    '#..##..#',
    '#..#...#',
    '.#.#..#.',
    '..####..',
  ],
  trophy: [
    '#.####.#',
    '#.####.#',
    '.######.',
    '..####..',
    '...##...',
    '...##...',
    '..####..',
    '.######.',
  ],
};
const COLORS = {
  red: 'var(--ka-red-bright)',
  gold: 'var(--ka-gold)',
  green: 'var(--ka-foliage)',
  purple: 'var(--ka-purple)',
};

export function itemIcon(item: Pick<RewardItem, 'glyph' | 'accent'>, size = 40) {
  if (item.glyph === 'spider')
    return html`<span style="color:${COLORS[item.accent]}">${spiderSvg(size)}</span>`;
  const rows = GLYPHS[item.glyph];
  return svg`<svg width=${size} height=${size} viewBox="0 0 8 8" shape-rendering="crispEdges" aria-hidden="true">
    ${rows.flatMap((row, y) => [...row].map((c, x) => (c === '#' ? svg`<rect x=${x} y=${y} width="1" height="1" fill=${COLORS[item.accent]}/>` : nothing)))}
  </svg>`;
}

const slotLabel: Record<RewardItem['slot'], string> = {
  badge: 'Badge',
  frame: 'Frame',
  emblem: 'Emblem',
  trophy: 'Trophy',
};

const funStyles = css`
  :host {
    display: grid;
    gap: 1.5rem;
  }
  .cards {
    list-style: none;
    padding: 0;
    margin: 0;
    display: grid;
    gap: 1rem;
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 16rem), 1fr));
  }
  .cards .panel {
    display: grid;
    gap: 0.5rem;
    align-content: start;
  }
  .row {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    align-items: center;
  }
  .avatar {
    display: inline-grid;
    place-items: center;
    width: 6rem;
    height: 6rem;
    border: 4px solid var(--ka-stone-600);
    background: var(--ka-stone-900);
    position: relative;
    color: var(--ka-red);
  }
  /* The skin's face (head front, hat layer on top), 8×8 pixels scaled up 9×. */
  .avatar .face {
    width: 72px;
    height: 72px;
    background-repeat: no-repeat;
    background-size: 576px 576px;
    image-rendering: pixelated;
  }
  .avatar.framed {
    border: 6px double var(--ka-gold);
    box-shadow: 0 0 0 3px var(--ka-red);
  }
  .avatar .badge {
    position: absolute;
    right: -0.8rem;
    bottom: -0.8rem;
  }
  .item {
    display: grid;
    justify-items: center;
    gap: 0.25rem;
    text-align: center;
    min-width: 6.5rem;
    padding: 0.5rem;
    border: 2px solid var(--ka-stone-600);
    background: var(--ka-stone-900);
    color: var(--ka-parchment);
    font: inherit;
  }
  button.item {
    cursor: pointer;
  }
  .item[aria-pressed='true'] {
    border-color: var(--ka-gold);
    box-shadow: inset 0 0 0 2px var(--ka-gold);
  }
  .locked {
    opacity: 0.55;
    filter: grayscale(1);
  }
  .small {
    font-size: 0.85rem;
  }
`;

const characterStyles = css`
  .top {
    margin: 0;
  }
  /* Grid children may shrink below their content (the item tray scrolls instead). */
  :host > * {
    min-width: 0;
  }
  .hero {
    display: grid;
    gap: 1rem;
    justify-items: center;
    text-align: center;
  }
  @media (min-width: 700px) {
    .hero {
      grid-template-columns: auto 1fr;
      justify-items: start;
      text-align: left;
      align-items: center;
    }
  }
  .stage {
    position: relative;
    display: grid;
    place-items: center;
    width: fit-content;
    min-width: min(12rem, 100%);
    max-width: 100%;
    min-height: 10rem;
    padding: 0.5rem;
    border: 4px solid var(--ka-stone-600);
    background: var(--ka-stone-900);
    box-sizing: border-box;
  }
  .stage.framed {
    border: 8px double var(--frame);
    box-shadow: 0 0 0 3px var(--ka-edge);
  }
  .stage ka-skin-viewer {
    width: 100%;
  }
  .spider {
    color: var(--ka-red);
    padding: 1.5rem 0;
  }
  .stage .badge {
    position: absolute;
    z-index: 1; /* above the 3D canvas */
    right: -1rem;
    bottom: -1rem;
    padding: 0.25rem;
    background: var(--ka-stone-800);
    border: 2px solid var(--ka-edge);
    box-shadow: var(--ka-bevel);
    line-height: 0;
  }
  .who h1 {
    margin: 0 0 0.25rem;
  }
  .who p {
    margin: 0 0 0.5rem;
  }
  .emblem {
    justify-content: center;
    gap: 0.5rem;
  }
  @media (min-width: 700px) {
    .emblem {
      justify-content: start;
    }
  }
  .tabs {
    display: flex;
    max-width: 28rem;
    gap: 0.5rem;
    margin-bottom: 0.75rem;
  }
  .tabs .btn {
    flex: 1 1 0;
    min-width: 0;
    padding-inline: 0.5em;
    justify-content: center;
  }
  .tabs .btn[aria-pressed='true'] {
    background: var(--ka-gold);
    color: var(--ka-ink-on-gold);
  }
  .tray {
    display: flex;
    gap: 0.75rem;
    overflow-x: auto;
    padding: 4px;
  }
  .tray .item {
    flex: none;
    min-height: 5.5rem;
  }
  .none {
    font: 600 1.5rem var(--ka-font-display);
    line-height: 40px;
  }
  details > summary.btn {
    box-sizing: border-box;
    list-style: none;
    margin-top: 0.75rem;
  }
  details > summary.btn::-webkit-details-marker {
    display: none;
  }
  .goals {
    list-style: none;
    margin: 0;
    padding: 0;
    display: grid;
    gap: 0.75rem;
  }
  .goal {
    display: grid;
    grid-template-columns: auto 1fr auto;
    gap: 0.75rem;
    align-items: center;
    padding-bottom: 0.75rem;
    border-bottom: 1px solid var(--ka-stone-700);
  }
  .goal .item {
    min-width: 0;
  }
  .what {
    display: grid;
    gap: 0.15rem;
  }
  .bar {
    display: block;
    height: 10px;
    margin-top: 0.25rem;
    background: var(--ka-stone-900);
    border: 2px solid var(--ka-edge);
  }
  .bar > span {
    display: block;
    height: 100%;
    background: var(--ka-foliage);
  }
`;

/** Shown instead of member content to visitors without a paired device. */
export const invitation = () =>
  html`<section class="panel">
    <h2>For invited friends</h2>
    <p>
      The Fun section is for friends of King Arachnid and Kynorum. Ask a grown-up to set up a family
      account and pair this device.
    </p>
    <p class="actions">
      <a class="btn primary" href="/pair">Pair this device</a>
      <a class="btn" href="/parents">For parents</a>
    </p>
  </section>`;

/** A skin's face as a small picture: the head's front with the hat layer over it. */
const faceOf = (url: string) => {
  const head = faceRect('head', 'front', 'base', 'classic');
  const hat = faceRect('head', 'front', 'outer', 'classic');
  return html`<span
    class="face"
    style="background-image: url(${url}), url(${url}); background-position: -${hat.x * 9}px -${hat.y * 9}px, -${head.x * 9}px -${head.y * 9}px"
  ></span>`;
};

/** Each game's picture on the hub (the same pixel glyphs as its rewards). */
const GAME_ICON: Record<string, Pick<RewardItem, 'glyph' | 'accent'>> = {
  redstone: { glyph: 'circuit', accent: 'red' },
  skin: { glyph: 'palette', accent: 'purple' },
  blueprint: { glyph: 'blueprint', accent: 'green' },
  minecarts: { glyph: 'cart', accent: 'gold' },
  expedition: { glyph: 'backpack', accent: 'green' },
};

const hubStyles = css`
  :host > * {
    min-width: 0;
  }
  .me {
    display: grid;
    gap: 0.75rem;
  }
  .me h1 {
    margin: 0;
  }
  .me .intro {
    margin: 0;
  }
  .who {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.75rem 1rem;
  }
  .who .avatar {
    width: 4rem;
    height: 4rem;
  }
  .who .name {
    font: 600 1.15rem var(--ka-font-display);
    margin: 0;
  }
  .recent {
    display: flex;
    gap: 0.25rem;
    align-items: center;
  }
  .games {
    list-style: none;
    margin: 0;
    padding: 0;
    display: grid;
    gap: 1rem;
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 18rem), 1fr));
  }
  .game {
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 0.25rem 0.75rem;
    align-content: start;
  }
  .game .icon {
    grid-row: span 2;
    line-height: 0;
    padding: 0.5rem;
    background: var(--ka-stone-900);
    border: 2px solid var(--ka-edge);
    align-self: start;
  }
  .game h2 {
    margin: 0;
    font-size: 1.25rem;
  }
  .game p {
    margin: 0;
  }
  .game .foot {
    grid-column: 1 / -1;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-top: 0.5rem;
  }
  .game .meter {
    flex: 1;
    display: grid;
    gap: 0.2rem;
  }
  .bar {
    display: block;
    height: 10px;
    background: var(--ka-stone-900);
    border: 2px solid var(--ka-edge);
  }
  .bar > span {
    display: block;
    height: 100%;
    background: var(--ka-foliage);
  }
`;

export class KaFunHub extends LitElement {
  static override properties = {
    view: { state: true },
    error: { state: true },
    face: { state: true },
  };
  declare view: FunView | null;
  declare error: string;
  /** The chosen skin as a data URL, for the avatar's face. */
  declare face: string | null;
  static override styles = [shared, funStyles, hubStyles];

  constructor() {
    super();
    this.view = currentFun();
    this.error = '';
    this.face = null;
  }
  override connectedCallback() {
    super.connectedCallback();
    if (currentKid())
      loadFun()
        .then(async (f) => {
          this.view = f;
          if (!f?.selectedSkinId) return;
          const s = await kidApi<{ png: string }>('GET', `/fun/skins/${f.selectedSkinId}`).catch(
            () => null, // keep the spider
          );
          if (s) this.face = `data:image/png;base64,${s.png}`;
        })
        .catch(
          () => (this.error = 'Could not load your progress. Check the connection and reload.'),
        );
  }

  override render() {
    const kid = currentKid();
    const solved = (id: string) =>
      Object.keys(this.view?.progress.solved[id as ActivityId] ?? {}).length;
    const recent = (this.view?.granted ?? [])
      .slice(-3)
      .reverse()
      .map(rewardById)
      .filter(Boolean) as RewardItem[];
    const badge = this.view?.equipped.badge ? rewardById(this.view.equipped.badge) : undefined;
    return html`<section class="panel me">
        <h1>Fun</h1>
        <p class="intro">
          Puzzles and games for friends. Everything here stays on the website — it never changes
          anything in Minecraft.
        </p>
        ${
          kid
            ? html`<div class="who">
                <span class="avatar ${this.view?.equipped.frame ? 'framed' : ''}" aria-hidden="true"
                  >${this.face ? faceOf(this.face) : spiderSvg(40)}${badge ? html`<span class="badge">${itemIcon(badge, 24)}</span>` : nothing}</span
                >
                <div>
                  <p class="name">${kid.nickname}</p>
                  ${
                    recent.length
                      ? html`<p class="recent small muted">
                          New:
                          ${recent.map((r) => html`<span role="img" aria-label=${r.name} title=${r.name}>${itemIcon(r, 20)}</span>`)}
                        </p>`
                      : nothing
                  }
                </div>
                <a class="btn" href="/fun/character">My character</a>
              </div>`
            : nothing
        }
        ${this.error ? html`<p role="alert" class="err">${this.error}</p>` : nothing}
      </section>
      ${kid ? nothing : invitation()}
      <ul class="games">
        ${ACTIVITIES.map((a) => {
          const done = solved(a.id);
          return html`<li class="panel game">
            <span class="icon" aria-hidden="true"
              >${itemIcon(GAME_ICON[a.id] ?? { glyph: 'trophy', accent: 'gold' }, 40)}</span
            >
            <h2>${a.title}</h2>
            <p class="small">${a.blurb}</p>
            ${
              kid
                ? html`<div class="foot">
                    ${
                      a.count
                        ? html`<span class="meter">
                            <span
                              class="bar"
                              role="progressbar"
                              aria-label="${a.title} progress"
                              aria-valuemin="0"
                              aria-valuemax=${a.count}
                              aria-valuenow=${done}
                              aria-valuetext="${done} of ${a.count} solved"
                              ><span style="width:${(100 * done) / a.count}%"></span
                            ></span>
                            <span class="small muted">${done} of ${a.count} solved</span>
                          </span>`
                        : html`<span class="meter small muted">Make and save your own.</span>`
                    }
                    <a class="btn green" href=${a.href}
                      >${a.count ? (done ? 'Play' : 'Start') : 'Create'}</a
                    >
                  </div>`
                : nothing
            }
          </li>`;
        })}
        <li class="panel game">
          <span class="icon" aria-hidden="true" style="color:var(--ka-red)">${spiderSvg(40)}</span>
          <h2>Bonus: spider hunt</h2>
          <p class="small">
            Hidden spider symbols are tucked away around the site. Can you find them?
          </p>
          <div class="foot">
            <span class="meter"></span><a class="btn" href="/lore">Go hunting</a>
          </div>
        </li>
      </ul>`;
  }
}
customElements.define('ka-fun-hub', KaFunHub);

export class KaFunCharacter extends LitElement {
  static override properties = {
    view: { state: true },
    msg: { state: true },
    busy: { state: true },
    skin: { state: true },
    tab: { state: true },
  };
  declare view: FunView | null;
  declare msg: string;
  declare busy: boolean;
  /** Which slot the Dress up tray shows. */
  declare tab: EquipSlot;
  /** The chosen Skin Studio design (data URL + model), shown to its owner only. */
  declare skin: { url: string; model: 'classic' | 'slim'; name: string } | null;
  static override styles = [shared, funStyles, characterStyles];

  constructor() {
    super();
    this.view = currentFun();
    this.msg = '';
    this.busy = false;
    this.skin = null;
    this.tab = 'badge';
  }
  override connectedCallback() {
    super.connectedCallback();
    if (currentKid())
      void loadFun()
        .then(async (f) => {
          this.view = f;
          if (f?.selectedSkinId) {
            const s = await kidApi<{ png: string; model: 'classic' | 'slim'; name: string }>(
              'GET',
              `/fun/skins/${f.selectedSkinId}`,
            );
            this.skin = { url: `data:image/png;base64,${s.png}`, model: s.model, name: s.name };
          }
        })
        .catch(() => (this.msg = 'Could not load your character.'));
  }

  private async equip(slot: EquipSlot, itemId: string | null) {
    this.busy = true;
    this.msg = '';
    try {
      const r = await kidApi<{ fun: FunView }>('PUT', '/fun/equip', { slot, itemId });
      setFun(r.fun);
      this.view = r.fun;
    } catch (e) {
      this.msg = e instanceof Error ? e.message : 'Could not save.';
    } finally {
      this.busy = false;
    }
  }

  /** The character as one picture: skin (or the spider), in its frame, with its badge. */
  private hero(kid: { nickname: string }) {
    const f = this.view;
    const badge = f?.equipped.badge ? rewardById(f.equipped.badge) : undefined;
    const frame = f?.equipped.frame ? rewardById(f.equipped.frame) : undefined;
    const emblem = f?.equipped.emblem ? rewardById(f.equipped.emblem) : undefined;
    const earned = REWARDS.filter((r) => f?.granted.includes(r.id)).length;
    return html`<section class="panel hero">
      <div
        class="stage ${frame ? 'framed' : ''}"
        style=${frame ? `--frame:${COLORS[frame.accent]}` : ''}
      >
        ${
          this.skin
            ? html`<ka-skin-viewer
                .skin=${this.skin.url}
                .model=${this.skin.model}
                label="${this.skin.name}, your character"
              ></ka-skin-viewer>`
            : html`<span class="spider" aria-hidden="true">${spiderSvg(96)}</span>`
        }
        ${badge ? html`<span class="badge" role="img" aria-label="Badge: ${badge.name}" title=${badge.name}>${itemIcon(badge, 40)}</span>` : nothing}
      </div>
      <div class="who">
        <h1>${kid.nickname}</h1>
        ${emblem ? html`<p class="row emblem">${itemIcon(emblem, 24)} ${emblem.name}</p>` : nothing}
        <p class="small muted">${earned} of ${REWARDS.length} earned</p>
        <p>
          <a class="btn" href="/fun/skin-studio"
            >${this.skin ? 'Change my skin' : 'Make my skin'}</a
          >
        </p>
      </div>
    </section>`;
  }

  /** One "Next to earn" row: what it is, how far along, and where to earn it. */
  private goal(r: RewardItem) {
    const { have, need } = r.progress(this.view!.progress);
    return html`<li class="goal">
      <span class="item locked" aria-hidden="true">${itemIcon(r, 32)}</span>
      <span class="what">
        <strong>${r.name}</strong>
        <span class="small muted">${slotLabel[r.slot]}</span>
        <span class="small">${r.unlock}</span>
        <span
          class="bar"
          role="progressbar"
          aria-label="${r.name} progress"
          aria-valuemin="0"
          aria-valuemax=${need}
          aria-valuenow=${have}
          aria-valuetext="${have} of ${need}"
          ><span style="width:${(100 * have) / need}%"></span
        ></span>
        <span class="small muted">${have} of ${need}</span>
      </span>
      <a class="btn green" href=${r.href}
        >${r.href === '/lore' ? 'Hunt' : r.href === '/fun/skin-studio' ? 'Create' : 'Play'}</a
      >
    </li>`;
  }

  override render() {
    const kid = currentKid();
    if (!kid) return invitation();
    const owned = new Set(this.view?.granted ?? []);
    const mine = (slot: RewardItem['slot']) =>
      REWARDS.filter((r) => r.slot === slot && owned.has(r.id));
    const slot = this.tab;
    const items = mine(slot);
    const next = this.view ? nextToEarn(this.view.progress, owned) : [];
    const trophies = mine('trophy');
    return html`<p class="small top"><a href="/fun">← Fun</a></p>
      ${this.hero(kid)} ${this.msg ? html`<p role="alert" class="err">${this.msg}</p>` : nothing}
      <section class="panel">
        <h2>Dress up</h2>
        <div class="tabs" role="group" aria-label="What to change">
          ${EQUIP_SLOTS.map(
            (s) =>
              html`<button class="btn" aria-pressed=${s === slot} @click=${() => (this.tab = s)}>
                ${slotLabel[s]}
              </button>`,
          )}
        </div>
        <div class="tray" role="group" aria-label="${slotLabel[slot]}s">
          ${
            items.length
              ? html`<button
                    class="item"
                    aria-pressed=${!this.view?.equipped[slot]}
                    ?disabled=${this.busy}
                    @click=${() => this.equip(slot, null)}
                  >
                    <span class="none" aria-hidden="true">–</span><span class="small">None</span>
                  </button>
                  ${items.map(
                    (r) =>
                      html`<button
                        class="item"
                        aria-pressed=${this.view?.equipped[slot] === r.id}
                        ?disabled=${this.busy}
                        @click=${() => this.equip(slot, r.id)}
                      >
                        ${itemIcon(r)}<span class="small">${r.name}</span>
                      </button>`,
                  )}`
              : html`<p class="muted small">
                  No ${slotLabel[slot].toLowerCase()}s yet. Earn one below!
                </p>`
          }
        </div>
      </section>
      <section class="panel">
        <h2>Trophy shelf</h2>
        ${
          trophies.length
            ? html`<p class="row">
                ${trophies.map((r) => html`<span class="item">${itemIcon(r)}<span class="small">${r.name}</span></span>`)}
              </p>`
            : html`<p class="muted small">
                Empty for now. Trophies are for finishing a whole game with gold.
              </p>`
        }
      </section>
      ${
        next.length
          ? html`<section class="panel">
              <h2>Next to earn</h2>
              <ul class="goals">
                ${next.slice(0, 3).map((r) => this.goal(r))}
              </ul>
              ${
                next.length > 3
                  ? html`<details>
                      <summary class="btn">Show all (${next.length - 3} more)</summary>
                      <ul class="goals">
                        ${next.slice(3).map((r) => this.goal(r))}
                      </ul>
                    </details>`
                  : nothing
              }
            </section>`
          : nothing
      }
      <p class="small muted">
        Badges, frames, emblems and trophies are website items. They do not appear in Minecraft,
        change your gear or give powers.
      </p>`;
  }
}

customElements.define('ka-fun-character', KaFunCharacter);
